import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import { signInAdmin, isValidEmail, getLockState, recordFailedAttempt, clearAttempts, onAuthStateChange } from '../services/auth';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/users';

  useEffect(() => {
    const unsub = onAuthStateChange((user) => {
      if (user) {
        navigate('/users', { replace: true });
      }
    });
    return () => unsub && unsub();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { locked, remainingMs } = getLockState();
    if (locked) {
      const secs = Math.ceil(remainingMs / 1000);
      setError(`Too many attempts. Please try again in ${secs}s.`);
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Please check your credentials.');
      return;
    }

    setStatus('loading');
    setError('');
    const res = await signInAdmin(email.trim(), password);
    setStatus('idle');
    if (res.ok) {
      clearAttempts();
      navigate(from, { replace: true });
    } else {
      recordFailedAttempt();
      setError(res.errorMsg);
    }
  };

  return (
    <div className="container" style={{ padding: '48px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <img src={logo} alt="Moro" className="logo-img" style={{ height: 72 }} />
      </div>
      <div className="content-card" style={{ maxWidth: 520, margin: '0 auto' }}>
        <h2 className="section-title" style={{ textAlign: 'center' }}>Admin Sign In</h2>
        <p className="section-subtitle" style={{ textAlign: 'center' }}>Use your admin credentials to continue.</p>
        <form onSubmit={handleSubmit} className="form-row full" style={{ gap: 10 }}>
          <input
            className="input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
          />
          <div style={{ position: 'relative', width: '100%', flex: 1, minWidth: 0 }}>
            <input
              className="input"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={{ paddingRight: 40, width: '100%' }}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: 0,
                width: 28,
                height: 28,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="3" fill="currentColor" />
                  <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="3" fill="currentColor" />
                </svg>
              )}
            </button>
          </div>
          <div className="form-actions" style={{ justifyContent: 'center' }}>
            <button className="btn btn-pill btn-gradient" type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Signing in…' : 'Sign In'}
            </button>
          </div>
          {error && <p style={{ color: '#c0392b', textAlign: 'center' }}>{error}</p>}
        </form>
        <p className="cta-foot" style={{ textAlign: 'center', marginTop: 10 }}>Having trouble? Contact support.</p>
      </div>
    </div>
  );
}